/**
 * Admin Service
 *
 * API calls for SuperAdmin interface.
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { coreApiClient } from '@/api/core-client';
import { apiClient } from '@/api/client';
import type {
  AdminCompaniesListResponse,
  AdminCompanyDetailResponse,
  AdminBrandDetailResponse,
  AdminPosConfigResponse,
  AdminSalesSyncStatusResponse,
  AdminSalesSyncInitResponse,
  AdminSalesSyncUnblockResponse,
  AdminCompanyListItem,
  AdminCompanyDetail,
  AdminBrandDetail,
  AdminPosConfig,
  SalesSyncStatusAdmin,
  CreateAdminCompanyInput,
  UpdateAdminCompanyInput,
  CreateAdminBrandInput,
  UpdateAdminBrandInput,
  UpsertAdminPosConfigInput,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  AdminCompanyListItem,
  AdminCompanyDetail,
  AdminBrandDetail,
  AdminPosConfig,
  SalesSyncStatusAdmin,
  CreateAdminCompanyInput,
  UpdateAdminCompanyInput,
  CreateAdminBrandInput,
  UpdateAdminBrandInput,
  UpsertAdminPosConfigInput,
};

type ApiResponse<T> = { success: true; data: T };

export const adminService = {
  // ---------------------------------------------------------------------------
  // Companies
  // ---------------------------------------------------------------------------

  /**
   * List all companies
   */
  async listCompanies(): Promise<AdminCompaniesListResponse> {
    const response = await coreApiClient.get<ApiResponse<AdminCompaniesListResponse>>(
      '/admin/companies'
    );
    return response.data.data;
  },

  /**
   * Get company detail with brands tree
   */
  async getCompany(id: string): Promise<AdminCompanyDetail> {
    const response = await coreApiClient.get<ApiResponse<AdminCompanyDetailResponse>>(
      `/admin/companies/${id}`
    );
    return response.data.data.company;
  },

  /**
   * Create a new company
   */
  async createCompany(data: CreateAdminCompanyInput): Promise<AdminCompanyDetail> {
    const response = await coreApiClient.post<ApiResponse<AdminCompanyDetailResponse>>(
      '/admin/companies',
      data
    );
    return response.data.data.company;
  },

  /**
   * Update a company
   */
  async updateCompany(
    id: string,
    data: UpdateAdminCompanyInput
  ): Promise<AdminCompanyDetail> {
    const response = await coreApiClient.patch<ApiResponse<AdminCompanyDetailResponse>>(
      `/admin/companies/${id}`,
      data
    );
    return response.data.data.company;
  },

  /**
   * Deactivate a company (soft delete)
   */
  async deactivateCompany(id: string): Promise<void> {
    await coreApiClient.delete(`/admin/companies/${id}`);
  },

  // ---------------------------------------------------------------------------
  // Brands
  // ---------------------------------------------------------------------------

  /**
   * Get brand detail
   */
  async getBrand(id: string): Promise<AdminBrandDetail> {
    const response = await coreApiClient.get<ApiResponse<AdminBrandDetailResponse>>(
      `/admin/brands/${id}`
    );
    return response.data.data.brand;
  },

  /**
   * Create a new brand for a company
   */
  async createBrand(
    companyId: string,
    data: CreateAdminBrandInput
  ): Promise<AdminBrandDetail> {
    const response = await coreApiClient.post<ApiResponse<AdminBrandDetailResponse>>(
      `/admin/companies/${companyId}/brands`,
      data
    );
    return response.data.data.brand;
  },

  /**
   * Update a brand
   */
  async updateBrand(id: string, data: UpdateAdminBrandInput): Promise<AdminBrandDetail> {
    const response = await coreApiClient.patch<ApiResponse<AdminBrandDetailResponse>>(
      `/admin/brands/${id}`,
      data
    );
    return response.data.data.brand;
  },

  /**
   * Deactivate a brand (soft delete)
   */
  async deactivateBrand(id: string): Promise<void> {
    await coreApiClient.delete(`/admin/brands/${id}`);
  },

  // ---------------------------------------------------------------------------
  // POS Config
  // ---------------------------------------------------------------------------

  /**
   * Get POS config for a brand
   */
  // ---------------------------------------------------------------------------
  // POS Config — served by bstock backend (table in bstock DB, not core)
  // ---------------------------------------------------------------------------

  async getPosConfig(brandId: string): Promise<AdminPosConfig | null> {
    const response = await apiClient.get<ApiResponse<AdminPosConfigResponse>>(
      `/admin/brands/${brandId}/pos-config`
    );
    return response.data.data.posConfig;
  },

  async upsertPosConfig(
    brandId: string,
    data: UpsertAdminPosConfigInput
  ): Promise<AdminPosConfig> {
    const response = await apiClient.put<ApiResponse<{ posConfig: AdminPosConfig }>>(
      `/admin/brands/${brandId}/pos-config`,
      data
    );
    return response.data.data.posConfig;
  },

  async deletePosConfig(brandId: string): Promise<void> {
    await apiClient.delete(`/admin/brands/${brandId}/pos-config`);
  },

  // ---------------------------------------------------------------------------
  // Sales Sync — served by bstock backend (table in bstock DB, not core)
  // ---------------------------------------------------------------------------

  async getSalesSyncStatus(brandId: string): Promise<SalesSyncStatusAdmin> {
    const response = await apiClient.get<ApiResponse<AdminSalesSyncStatusResponse>>(
      `/admin/brands/${brandId}/sales-sync-status`
    );
    return response.data.data.syncStatus;
  },

  async initSalesSync(brandId: string, startDate: string): Promise<{ batchId: string }> {
    const response = await apiClient.post<ApiResponse<AdminSalesSyncInitResponse>>(
      `/admin/brands/${brandId}/sales-sync-init`,
      { startDate }
    );
    return response.data.data;
  },

  async unblockSalesSync(brandId: string): Promise<{ unblockedBatchId: string }> {
    const response = await apiClient.post<ApiResponse<AdminSalesSyncUnblockResponse>>(
      `/admin/brands/${brandId}/sales-sync-unblock`
    );
    return response.data.data;
  },
};
